import React from "react";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RowTypeField from "./RowTypeField";

function SectionHeader({ title, description }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant='subtitle1' fontWeight={700}>
        {title}
      </Typography>
      <Typography variant='body2' color='text.secondary'>
        {description}
      </Typography>
    </Stack>
  );
}

function getScheduleOptionSx(theme, { isSelected, isRecommended }) {
  return {
    border: 1,
    borderRadius: 2,
    py: 1.75,
    px: 1.75,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 1.5,
    alignItems: "center",
    textAlign: "left",
    borderColor: "divider",
    bgcolor: "background.paper",
    ...(isRecommended && {
      borderColor: alpha(theme.palette.success.main, 0.45),
      bgcolor: alpha(theme.palette.success.main, 0.08),
    }),
    ...(isSelected && {
      borderColor: "primary.main",
      boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
    }),
  };
}

const sectionPaperSx = {
  p: 2.5,
  display: "grid",
  gap: 2,
  borderRadius: 2,
};

export default function ExistingScheduleStep({
  schedules,
  selectedScheduleType,
  selectedSchedule,
  setSelectedSchedule,
  existingScheduleStep,
  control,
  errors,
  companyType,
  isRowTypeDisabled,
  rowTypeRequired,
  editMode,
  row,
  recommendedSchedule,
}) {
  const theme = useTheme();

  if (existingScheduleStep === 1) {
    return (
      <Paper variant='outlined' component='section' sx={sectionPaperSx}>
        <SectionHeader
          title='Choose row type'
          description={`Complete the row classification for "${selectedSchedule.scheduleTitle}".`}
        />
        <RowTypeField
          selectedScheduleType={selectedScheduleType}
          control={control}
          errors={errors}
          companyType={companyType}
          disabled={isRowTypeDisabled}
          required={rowTypeRequired}
        />
      </Paper>
    );
  }

  return (
    <Paper variant='outlined' component='section' sx={sectionPaperSx}>
      <SectionHeader
        title='Select a schedule'
        description={`Pick an existing ${selectedScheduleType} schedule to bind this row.`}
      />

      {schedules.length > 0 ? (
        <List
          disablePadding
          sx={{
            display: "grid",
            gap: 1.25,
            maxHeight: 280,
            overflow: "auto",
          }}
        >
          {schedules.map((schedule) => {
            const isCurrent =
              editMode &&
              row?.schedule &&
              row.schedule.sch_id === schedule.sch_id;
            const isSelected = selectedSchedule.sch_id === schedule.sch_id;
            const isRecommended = false;

            return (
              <ListItemButton
                key={schedule.sch_id}
                selected={isSelected}
                onClick={() =>
                  setSelectedSchedule({
                    sch_id: schedule.sch_id,
                    scheduleTitle: schedule.scheduleTitle,
                    scheduleNo: schedule.scheduleNo,
                  })
                }
                sx={getScheduleOptionSx(theme, { isSelected, isRecommended })}
              >
                <ListItemText
                  primary={schedule.scheduleTitle}
                  secondary={`Schedule No: ${schedule.scheduleNo}`}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    fontSize: 14,
                    noWrap: true,
                  }}
                  secondaryTypographyProps={{ fontSize: 12 }}
                  sx={{ m: 0 }}
                />
                <Stack
                  direction='row'
                  spacing={0.75}
                  flexWrap='wrap'
                  justifyContent='flex-end'
                >
                  {isRecommended && (
                    <Chip
                      label='Recommended'
                      size='small'
                      color='success'
                      sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                    />
                  )}
                  {isCurrent && (
                    <Chip
                      label='Current'
                      size='small'
                      color='primary'
                      variant='outlined'
                      sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
                    />
                  )}
                </Stack>
              </ListItemButton>
            );
          })}
        </List>
      ) : (
        <Box
          sx={{
            py: 4,
            px: 2,
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            No existing {selectedScheduleType} schedule is available.
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
